import { __read } from "tslib";
import * as PointUtils from '../point';
/**
 * Creates a new Matrix from the given params
 */
export function create(a, b, c, d, tx, ty) {
    if (a === void 0) { a = 1; }
    if (b === void 0) { b = 0; }
    if (c === void 0) { c = 0; }
    if (d === void 0) { d = 1; }
    if (tx === void 0) { tx = 0; }
    if (ty === void 0) { ty = 0; }
    return [a, b, c, d, tx, ty];
}
// given matrixC which is equal to matrixA * matrixB returns matrixA
export function divideLeft(matrixA, matrixB) {
    return multiply(matrixA, invert(matrixB));
}
// given matrixC which is equal to matrixA * matrixB returns matrixB
export function divideRight(matrixA, matrixB) {
    return multiply(invert(matrixB), matrixA);
}
export function getDeterminant(_a) {
    var _b = __read(_a, 4), a = _b[0], b = _b[1], c = _b[2], d = _b[3];
    return a * d - c * b;
}
export function getScaling(matrix) {
    var _a = __read(matrix, 4), a = _a[0], b = _a[1], c = _a[2], d = _a[3];
    var determinant = getDeterminant(matrix);
    if (a !== 0 || b !== 0) {
        var r = Math.sqrt(a * a + b * b);
        return [r, determinant / r];
    }
    if (c !== 0 || d !== 0) {
        var s = Math.sqrt(c * c + d * d);
        return [determinant / s, s];
    }
    return [0, 0];
}
export function multiply(_a, _b) {
    var _c = __read(_a, 6), a1 = _c[0], b1 = _c[1], c1 = _c[2], d1 = _c[3], tx1 = _c[4], ty1 = _c[5];
    var _d = __read(_b, 6), a2 = _d[0], b2 = _d[1], c2 = _d[2], d2 = _d[3], tx2 = _d[4], ty2 = _d[5];
    return create(a2 * a1 + b2 * c1, a2 * b1 + b2 * d1, c2 * a1 + d2 * c1, c2 * b1 + d2 * d1, tx1 + tx2 * a1 + ty2 * c1, ty1 + tx2 * b1 + ty2 * d1);
}
// WARNING: will throw if the matrix is not invertible!
export function invert(matrix) {
    var determinant = getDeterminant(matrix);
    var _a = __read(matrix, 6), a = _a[0], b = _a[1], c = _a[2], d = _a[3], tx = _a[4], ty = _a[5];
    // We explicitly add 0 becasue division or multiplication by 0
    // may result in js returning a negative 0
    // http://www.ecma-international.org/ecma-262/5.1/#sec-8.5
    return create(d / determinant + 0, -b / determinant + 0, -c / determinant + 0, a / determinant + 0, (c * ty - d * tx) / determinant + 0, (b * tx - a * ty) / determinant + 0);
}
export function isInvertible(matrix) {
    var determinant = getDeterminant(matrix);
    var _a = __read(matrix, 6), tx = _a[4], ty = _a[5];
    return (!!determinant &&
        !Number.isNaN(determinant) &&
        Number.isFinite(tx) &&
        Number.isFinite(ty));
}
export function scale(matrix, scaleAmount, center) {
    var _a = __read(PointUtils.coerce(scaleAmount), 2), scaleX = _a[0], scaleY = _a[1];
    var _b = __read(center ? translate(matrix, center) : matrix, 6), a = _b[0], b = _b[1], c = _b[2], d = _b[3], tx = _b[4], ty = _b[5];
    var scaledMatrix = [
        a * scaleX,
        b * scaleX,
        c * scaleY,
        d * scaleY,
        tx,
        ty,
    ];
    return center
        ? translate(scaledMatrix, PointUtils.negate(center))
        : scaledMatrix;
}
// Returns matrixC such that matrixA * matrixB is equivalent to matrixB * matrixC. This is useful
// if you want to transform an item for instance as if it was at the root of the scene.
export function shiftLeft(matrixA, matrixB) {
    return multiply(divideRight(matrixA, matrixB), matrixB);
}
export function translate(_a, p, out) {
    var _b = __read(_a, 6), a = _b[0], b = _b[1], c = _b[2], d = _b[3], tx = _b[4], ty = _b[5];
    if (out === void 0) { out = create(a, b, c, d); }
    var _c = __read(PointUtils.coerce(p), 2), x = _c[0], y = _c[1];
    out[4] = tx + (x * a + y * c);
    out[5] = ty + (x * b + y * d);
    return out;
}
